import { DocumentTextIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Routes, useNavigate, useParams } from 'react-router-dom';

import { getContracts } from '../../api/contracts';
import { getModuleCategories } from '../../api/modules';

import { classNames } from '../../components/helpers/functions';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';

import Badge from '../../components/Badge';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import DataTable from '../../components/DataTable';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSearchParams from '../../hooks/useSearchParams';
import usePrevious from '../../hooks/usePrevious';

import ContractForm from './ContractForm';

const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;

export const Contracts = () => {
	const navigate = useNavigate();
	const { id, tab } = useParams();
	const [currentTab, setCurrentTab] = useState(null);
	const [lastTab, setLastTab] = useState(null);

	const { open: contractOverviewOpen, setOpen: openContractModal } =
		useModal('contractOverview');

	const { open: isAddNewContractModalOpen, setOpen: openAddNewContractModal } =
		useModal('contractAdd');

	const {
		page: pageFilter,
		category: categoryFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
	} = useSearchParams();

	const { value: contracts, execute: refreshContracts } = useAsync(
		getContracts,
		{
			defaultPayload: { categoryId: categoryFilter || 'active', pageSize: null },
		},
	);
	const { value: categories, execute: onRefreshCategories } = useAsync(
		getModuleCategories,
		{
			defaultPayload: { id: CONTRACT_MODULE_ID },
			silent: true,
		},
	);

	const prevContractOverviewOpen = usePrevious(contractOverviewOpen);
	useEffect(() => {
		if (!contractOverviewOpen && prevContractOverviewOpen) {
			handleTabChange(lastTab || 'active');

			refreshContracts({
				categoryId: lastTab || 'active',
			});
		}
	}, [contractOverviewOpen]);

	useEffect(() => {
		if (id) {
			openContractModal({
				id,
				path: `/manage/contracts${categoryFilter ? `?category=${categoryFilter}` : ''}`,
			});
		}
	}, [id]);

	useEffect(() => {
		refreshContracts(
			{
				page: pageFilter,
				categoryId: categoryFilter || 'active',
				sortBy: sortByFilter,
				sortOrder: sortOrderFilter,
			},
			null,
			{
				silent: true,
			},
		);

		setCurrentTab(categoryFilter || 'active');
	}, [pageFilter, categoryFilter, sortByFilter, sortOrderFilter]);

	const prevNewContractModal = usePrevious(isAddNewContractModalOpen);
	useEffect(() => {
		if (!isAddNewContractModalOpen && prevNewContractModal) {
			refreshContracts(
				{
					page: pageFilter,
					categoryId: categoryFilter || 'active',
					sortBy: sortByFilter,
					sortOrder: sortOrderFilter,
				},
				null,
				{
					silent: true,
				},
			);
		}
	}, [isAddNewContractModalOpen]);

	const handleTabChange = (tab) => {
		const search = new URLSearchParams();

		if (tab && tab !== 'active') {
			search.append('category', tab);
			navigate({ search: search ? search.toString() : '' });
		} else {
			// 'active' is the default, so clear the search params
			navigate({ search: '' });
		}

		setLastTab(tab);
	};

	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[
					{
						name: 'Add New Contract',
						onClick: () => openAddNewContractModal(),
					},
				]}
				divider={false}
			>
				Manage Contracts
			</PageHeading>
			<div className="grid grid-cols-12">
				<div className="col-span-2">
					<nav
						className={classNames('flex flex-1 flex-col mt-2')}
						aria-label="Sidebar"
					>
						<ul role="list" className="-mx-2 space-y-1">
							{[
								{
									name: 'Active',
									onClick: () => {
										handleTabChange('active');
									},
									current: currentTab === 'active' || !currentTab,
								},
								{
									name: 'Expiring Soon',
									onClick: () => {
										handleTabChange('expiring');
									},
									current: currentTab === 'expiring',
								},
								{
									name: 'Expired',
									onClick: () => {
										handleTabChange('expired');
									},
									current: currentTab === 'expired',
								},
								{
									name: 'Voided',
									onClick: () => {
										handleTabChange('voided');
									},
									current: currentTab === 'voided',
								},
								{
									name: 'All',
									onClick: () => {
										handleTabChange('all');
									},
									current: currentTab === 'all',
									divider: 'CATEGORIES',
								},

								...(categories
									? categories?.map((category) => ({
											name: category?.name,
											onClick: () => {
												handleTabChange(category?.id);
											},
											current: currentTab === category?.id,
										}))
									: []),

								{
									name: 'Uncategorized',
									onClick: () => {
										handleTabChange('uncategorized');
									},
									current: currentTab === 'uncategorized',
									//count: documentBreakdown?.uncategorized?.length,
								},
							].map((item) => {
								return (
									<>
										<li key={item.name}>
											<div
												onClick={() => item.onClick()}
												className={classNames(
													item.current
														? 'bg-gray-50 text-indigo-600'
														: 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
													'group flex gap-x-3 rounded-md p-2 text-xs leading-6 font-medium cursor-pointer',
												)}
											>
												{item.icon && (
													<item.icon
														className={classNames(
															item.current
																? 'text-indigo-600'
																: 'text-gray-400 group-hover:text-indigo-600',
															'h-6 w-6 shrink-0',
														)}
														aria-hidden="true"
													/>
												)}
												{item.name}
												{typeof item.count !== 'undefined' ? (
													<span
														className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xxs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200"
														aria-hidden="true"
													>
														{item.count || 0}
													</span>
												) : null}
											</div>
										</li>
										{item?.divider ? (
											<div className="flex items-center">
												{typeof item?.divider === 'string' ? (
													<div className=" text-xxs text-gray-400 mr-2 pl-2 my-3">
														{item?.divider}
													</div>
												) : null}
												<div className="flex-1 my-2 border-b border-gray-100"></div>
											</div>
										) : null}
									</>
								);
							})}
						</ul>
					</nav>
				</div>
				<div className="col-span-10 mx-2 px-4 mt-4">
					<DataTable
						data={{
							rows: contracts?.rows,
						}}
						manualSorting={false}
						columns={[
							{
								header: 'Name',
								accessorKey: 'name',
								cell: (info) => info?.getValue() || 'N/A 3',
							},
							{
								header: 'Status',
								accessorKey: 'status',
								cell: (info) => {
									const contract = info?.row?.original;
									const contractEndDate = contract?.endDate;
									const contractStartDate = contract?.startDate;

									const isExpired =
										moment().isAfter(moment(contractEndDate).endOf('d')) ||
										contract?.active === false;

									const isExpiringSoon = moment().isAfter(
										moment(contractEndDate).subtract(30, 'days').endOf('d'),
									);

									const isUpcoming = moment().isBefore(
										moment(contractStartDate),
									);

									return (
										<Badge
											isBeta
											size="xxs"
											status={
												isExpired
													? 'error'
													: isExpiringSoon
														? 'warning'
														: isUpcoming
															? 'default'
															: 'success'
											}
										>
											{contract?.active === false
												? 'Voided'
												: isExpired
													? `Expired ${moment(contractEndDate).fromNow()}`
													: isExpiringSoon
														? `Expiring ${moment(contractEndDate).fromNow()}`
														: isUpcoming
															? `Upcoming ${moment(contractStartDate).fromNow()}`
															: 'Active'}
										</Badge>
									);
								},
							},
							{
								header: 'Expires',
								accessorKey: 'nextEvent',
								sortingFn: (a, b) => {
									const aDate = a?.original?.endDate;
									const bDate = b?.original?.endDate;

									return moment.utc(aDate).diff(moment.utc(bDate));
								},
								cell: (info) => {
									const date = info?.row?.original?.endDate;

									return date ? moment(date).format('MM/DD/YYYY') : '';
								},
							},

							{
								header: 'Type',
								accessorKey: 'category.name',
								cell: (info) => info?.getValue() || 'Uncategorized',
							},
							{
								header: '',
								accessorKey: 'documents',
								enableSorting: false,
								cell: (info) => {
									const documents = info?.row?.original?.documents;

									if (documents?.length) {
										return (
											<div
												className="cursor-pointer flex items-center"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													window.open(documents[0]?.location, '_blank');
												}}
											>
												<DocumentTextIcon className="w-5 h-5 text-red-600 mr-1.5" />{' '}
												View
											</div>
										);
									}
									return '';
									//info?.getValue() || 'Uncategorized',
								},
							},
						]}
						onRow={(row) => {
							if (row?.original?.active) {
								navigate(`/manage/contracts/${row.original?.id}`);
							}
						}}
						hasCheckboxes={true}
						usePagination={false}
						useExport={false}
						useSearch={false}
						disableTitle={true}
						initialState={{
							sortBy: [
								{
									id: 'nextEvent',
									desc: false,
								},
							],
						}}
					/>
				</div>
			</div>
		</MainContent>
	);
};

export default {};

/*export default () => (
	<Routes>
		<ProtectedRoute
			exact
			path={`/manage/contracts/new`}
			component={ContractForm}
		/>
		<ProtectedRoute
			exact
			path={`/manage/contracts/:id/edit`}
			component={ContractForm}
		/>
		<ProtectedRoute exact path={`/manage/contracts`} component={Contracts} />
		<ProtectedRoute
			exact
			path={`/manage/contracts/:id`}
			component={Contracts}
		/>
	</Routes>
);*/
